/**
 * Custom hook for OKR filter state management
 * Manages filter state, URL sync, filter metadata, and role-based default filtering
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { okrService } from '../services';
import { useTeamHierarchyIds } from './useTeamHierarchy';
import { parseFiltersFromURL, filtersToURLParams } from '../utils/okrListURL';
import type { OKRFilters } from '../types/okr';

const DEFAULT_FILTERS: OKRFilters = {
  search: '',
  types: [],
  statuses: [],
  ownerIds: [],
  departmentIds: [],
  cycleIds: [],
  progressMin: undefined,
  progressMax: undefined,
  startDate: '',
  endDate: '',
  filterMode: 'AND',
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  page: 1,
  limit: 20,
};

/**
 * Get role-based default ownerIds filter
 * - account_admin: No filter (sees all OKRs)
 * - manager: Own OKRs + their whole team hierarchy's OKRs (every level, not
 *   just direct reports — that is what the endpoint returns)
 * - employee/viewer: Only own OKRs
 */
const getRoleBasedOwnerIds = (
  role: string | undefined,
  userId: string | undefined,
  teamHierarchyIds: string[]
): string[] | undefined => {
  if (!userId) return undefined;

  switch (role) {
    case 'account_admin':
    case 'super_admin':
      // Admins see all OKRs - no ownerIds filter
      return undefined;
    case 'manager':
      // Managers see own OKRs + full team hierarchy OKRs
      return [userId, ...teamHierarchyIds];
    case 'employee':
    case 'viewer':
    default:
      // Employees/viewers see only own OKRs
      return [userId];
  }
};

/**
 * Hook for managing OKR filters with URL sync and role-based defaults
 */
export const useOKRFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { data: teamHierarchyIds = [] } = useTeamHierarchyIds();

  // Calculate role-based default ownerIds
  const roleBasedOwnerIds = useMemo(
    () => getRoleBasedOwnerIds(user?.role, user?.id, teamHierarchyIds),
    [user?.role, user?.id, teamHierarchyIds]
  );

  // Check if URL has explicit ownerIds filter
  const urlHasOwnerFilter = searchParams.has('ownerIds');
  // Check if URL has explicit cycleIds filter (for current-cycle default behavior)
  const urlHasCycleFilter = searchParams.has('cycleIds');

  // Initialize filters with URL params, applying role-based defaults if no URL filter
  const [filters, setFilters] = useState<OKRFilters>(() => {
    const urlFilters = parseFiltersFromURL(searchParams);
    return {
      ...DEFAULT_FILTERS,
      ...urlFilters,
      // Apply role-based ownerIds only if URL doesn't specify ownerIds
      ownerIds: urlFilters.ownerIds ?? roleBasedOwnerIds ?? [],
    };
  });

  // Update filters when role-based ownerIds change (e.g., on login or direct reports load)
  useEffect(() => {
    // Only apply role-based defaults if URL doesn't have explicit ownerIds
    if (!urlHasOwnerFilter && roleBasedOwnerIds !== undefined) {
      setFilters((prev) => ({
        ...prev,
        ownerIds: roleBasedOwnerIds,
      }));
    }
  }, [roleBasedOwnerIds, urlHasOwnerFilter]);

  // Fetch filter metadata
  const {
    data: metadata,
    isLoading: isLoadingMetadata,
    isError: isMetadataError,
  } = useQuery({
    queryKey: ['okr-filter-metadata'],
    queryFn: () => okrService.getFilterMetadata(),
  });

  // Default cycleIds filter to the active cycle on initial mount (when URL doesn't
  // pin one). Ensures Dashboard / OKR List / Alignment Map all open in the current
  // cycle by default. Tracks first-apply to avoid re-applying after the user
  // explicitly clears the cycle filter via "View all cycles".
  const cycleDefaultAppliedRef = useRef(urlHasCycleFilter);
  useEffect(() => {
    if (cycleDefaultAppliedRef.current) return;
    const cycles = (metadata as { cycles?: Array<{ id: string; isActive?: boolean }> } | undefined)?.cycles;
    if (!cycles) return;
    const active = cycles.find((c) => c.isActive);
    cycleDefaultAppliedRef.current = true;
    if (!active) return;
    setFilters((prev) => {
      if (prev.cycleIds && prev.cycleIds.length > 0) return prev;
      return { ...prev, cycleIds: [active.id] };
    });
  }, [metadata]);

  // Sync filters to URL whenever they change
  useEffect(() => {
    const params = filtersToURLParams(filters);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters: Partial<OKRFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      // Reset to page 1 when filters change (except when page itself is being updated)
      page: 'page' in newFilters ? newFilters.page : 1,
    }));
  }, []);

  /**
   * Remove a specific filter
   */
  const removeFilter = useCallback((filterKey: keyof OKRFilters, value?: string) => {
    setFilters((prev) => {
      const updated = { ...prev };

      if (filterKey === 'types' || filterKey === 'statuses' || filterKey === 'ownerIds' || filterKey === 'departmentIds' || filterKey === 'cycleIds') {
        // Remove specific value from array
        if (value && updated[filterKey]) {
          updated[filterKey] = (updated[filterKey] as string[]).filter((v) => v !== value);
        }
      } else if (filterKey === 'progressMin' || filterKey === 'progressMax') {
        // Clear both progress filters
        updated.progressMin = undefined;
        updated.progressMax = undefined;
      } else if (filterKey === 'startDate' || filterKey === 'endDate') {
        // Clear both date filters
        updated.startDate = '';
        updated.endDate = '';
      } else {
        // Clear single value filter
        (updated as Record<string, unknown>)[filterKey] = DEFAULT_FILTERS[filterKey as keyof typeof DEFAULT_FILTERS];
      }

      return { ...updated, page: 1 };
    });
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useCallback(() => {
    return (
      (filters.search && filters.search.length > 0) ||
      (filters.types && filters.types.length > 0) ||
      (filters.statuses && filters.statuses.length > 0) ||
      (filters.ownerIds && filters.ownerIds.length > 0) ||
      (filters.departmentIds && filters.departmentIds.length > 0) ||
      (filters.cycleIds && filters.cycleIds.length > 0) ||
      filters.progressMin !== undefined ||
      filters.progressMax !== undefined ||
      (filters.startDate && filters.startDate.length > 0) ||
      (filters.endDate && filters.endDate.length > 0)
    );
  }, [filters]);

  return {
    filters,
    metadata,
    isLoadingMetadata,
    isMetadataError,
    updateFilters,
    removeFilter,
    clearFilters,
    hasActiveFilters: hasActiveFilters(),
  };
};
